import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { Statistics, HandHistoryEntry, AchievementProgress } from '../types';
import * as sounds from '../utils/sounds';
// Note: Firebase persistence is handled by gameStore subscriber, not this store

export const DEFAULT_STATISTICS: Statistics = {
  handsPlayed: 0,
  handsWon: 0,
  handsLost: 0,
  handsPushed: 0,
  blackjacks: 0,
  totalWagered: 0,
  totalWon: 0,
  countAccuracy: 0,
  countAttempts: 0,
  betSizingAccuracy: 0,
  betsPlaced: 0,
  perfectBets: 0,
  countErrors: 0,
  sessionStartTime: Date.now(),
  biggestWin: 0,
  biggestLoss: 0,
  averageResponseTime: 0,
  fastestCorrectTime: null,
  consecutiveCorrect: 0,
  bestStreak: 0,
  insuranceBetsWon: 0,
  insuranceBetsLost: 0,
  splitHandsPlayed: 0,
  currentBankroll: 10000,
  // Scenario practice statistics
  scenariosAttempted: 0,
  scenariosCorrect: 0,
  scenariosByCategory: {
    'basic': { attempted: 0, correct: 0 },
    'soft-hands': { attempted: 0, correct: 0 },
    'splits': { attempted: 0, correct: 0 },
    'advanced': { attempted: 0, correct: 0 },
    'deviations': { attempted: 0, correct: 0 },
    'insurance': { attempted: 0, correct: 0 }
  },
  scenariosByDifficulty: {
    'easy': { attempted: 0, correct: 0 },
    'medium': { attempted: 0, correct: 0 },
    'hard': { attempted: 0, correct: 0 }
  },
  totalXP: 0,
  level: 1,
  drillsCompleted: 0
};

interface StatisticsStore {
  statistics: Statistics;
  handHistory: HandHistoryEntry[];
  achievements: AchievementProgress[];

  // Actions
  updateStatistics: (updates: Partial<Statistics>) => void;
  resetStatistics: () => void;
  incrementStat: (stat: keyof Statistics, amount?: number) => void;
  
  // Hand tracking
  recordHandResult: (result: 'win' | 'loss' | 'push' | 'blackjack', bet: number, profit: number) => void;
  updateBankroll: (bankroll: number) => void;
  
  // Counting tracking
  recordCountAttempt: (isCorrect: boolean, responseTime?: number) => void;
  recordPerfectBet: () => void;
  
  // Scenario tracking
  updateScenarioStats: (category: string, difficulty: string, isCorrect: boolean) => void;
  
  // History
  addHandHistoryEntry: (entry: HandHistoryEntry) => void;
  clearHandHistory: () => void;
  
  // Achievements
  checkAchievements: () => void;
  unlockAchievement: (achievementId: string) => void;
}

export const useStatisticsStore = create<StatisticsStore>()(
  subscribeWithSelector((set, get) => ({
    statistics: { ...DEFAULT_STATISTICS },
    handHistory: [],
    achievements: [],

    updateStatistics: (updates) => {
      set((state) => {
        const newStats = { ...state.statistics, ...updates };
        return { statistics: newStats };
      });
    },

    resetStatistics: () => {
      const freshStats = { ...DEFAULT_STATISTICS, sessionStartTime: Date.now() };
      set({
        statistics: freshStats,
        handHistory: []
      });
    },

    incrementStat: (stat, amount = 1) => {
      set((state) => {
        const currentValue = state.statistics[stat];
        if (typeof currentValue === 'number') {
          const newStats = {
            ...state.statistics,
            [stat]: currentValue + amount
          };
          return { statistics: newStats };
        }
        return state;
      });
    },

    recordHandResult: (result, bet, profit) => {
      set((state) => {
        const newStats = { ...state.statistics };
        newStats.handsPlayed++;
        newStats.totalWagered += bet;

        switch (result) {
          case 'win':
            newStats.handsWon++;
            newStats.totalWon += profit;
            newStats.biggestWin = Math.max(newStats.biggestWin, profit);
            break;
          case 'blackjack':
            newStats.handsWon++;
            newStats.blackjacks++;
            newStats.totalWon += profit;
            newStats.biggestWin = Math.max(newStats.biggestWin, profit);
            break;
          case 'loss':
            newStats.handsLost++;
            newStats.biggestLoss = Math.max(newStats.biggestLoss, bet);
            break;
          case 'push':
            newStats.handsPushed++;
            break;
        }

        return { statistics: newStats };
      });
    },

    updateBankroll: (bankroll) => {
      set((state) => {
        const newStats = { ...state.statistics, currentBankroll: bankroll };
        return { statistics: newStats };
      });
    },

    recordCountAttempt: (isCorrect, responseTime) => {
      set((state) => {
        const newStats = { ...state.statistics };
        
        // Increment count attempts
        newStats.countAttempts = (newStats.countAttempts || 0) + 1;

        if (isCorrect) {
          newStats.consecutiveCorrect++;
          newStats.bestStreak = Math.max(newStats.bestStreak, newStats.consecutiveCorrect);

          if (responseTime !== undefined) {
            if (newStats.fastestCorrectTime === null || responseTime < newStats.fastestCorrectTime) {
              newStats.fastestCorrectTime = responseTime;
            }
            // Update average response time
            const totalCorrect = newStats.countAttempts - newStats.countErrors;
            if (totalCorrect > 0) {
              newStats.averageResponseTime =
                (newStats.averageResponseTime * (totalCorrect - 1) + responseTime) / totalCorrect;
            }
          }
        } else {
          newStats.countErrors++;
          newStats.consecutiveCorrect = 0;
        }

        // Calculate accuracy percentage based on actual count attempts
        if (newStats.countAttempts > 0) {
          newStats.countAccuracy = ((newStats.countAttempts - newStats.countErrors) / newStats.countAttempts) * 100;
        }

        return { statistics: newStats };
      });
    },

    recordPerfectBet: () => {
      set((state) => {
        const newStats = {
          ...state.statistics,
          perfectBets: state.statistics.perfectBets + 1
        };
        return { statistics: newStats };
      });
    },

    updateScenarioStats: (category, difficulty, isCorrect) => {
      set((state) => {
        const newStats = { ...state.statistics };

        // Update overall scenario stats
        newStats.scenariosAttempted++;
        if (isCorrect) {
          newStats.scenariosCorrect++;
        }

        // Update category stats
        if (newStats.scenariosByCategory[category]) {
          newStats.scenariosByCategory[category].attempted++;
          if (isCorrect) {
            newStats.scenariosByCategory[category].correct++;
          }
        }

        // Update difficulty stats
        if (newStats.scenariosByDifficulty[difficulty]) {
          newStats.scenariosByDifficulty[difficulty].attempted++;
          if (isCorrect) {
            newStats.scenariosByDifficulty[difficulty].correct++;
          }
        }

        return { statistics: newStats };
      });
    },

    addHandHistoryEntry: (entry) => {
      set((state) => {
        // Keep history limited to last 50 hands
        const newHistory = [...state.handHistory, entry].slice(-50);
        return { handHistory: newHistory };
      });
    },

    clearHandHistory: () => {
      set({ handHistory: [] });
    },

    checkAchievements: () => {
      const state = get();
      const { statistics, achievements } = state;

      // Define all achievements
      const allAchievements = [
        // Milestone achievements
        { id: 'first-hand', name: 'First Hand', requirement: 1, check: () => statistics.handsPlayed >= 1 },
        { id: 'hands-10', name: 'Getting Started', requirement: 10, check: () => statistics.handsPlayed >= 10 },
        { id: 'hands-25', name: 'Novice Player', requirement: 25, check: () => statistics.handsPlayed >= 25 },
        { id: 'hands-50', name: 'Apprentice', requirement: 50, check: () => statistics.handsPlayed >= 50 },
        { id: 'hands-100', name: 'Card Counter', requirement: 100, check: () => statistics.handsPlayed >= 100 },
        { id: 'hands-250', name: 'Seasoned Player', requirement: 250, check: () => statistics.handsPlayed >= 250 },
        { id: 'hands-500', name: 'Dedicated Player', requirement: 500, check: () => statistics.handsPlayed >= 500 },
        { id: 'hands-1000', name: 'Professional', requirement: 1000, check: () => statistics.handsPlayed >= 1000 },
        { id: 'hands-2500', name: 'Master Counter', requirement: 2500, check: () => statistics.handsPlayed >= 2500 },
        { id: 'hands-5000', name: 'Legend', requirement: 5000, check: () => statistics.handsPlayed >= 5000 },

        // Gameplay achievements
        { id: 'first-blackjack', name: 'Lucky!', requirement: 1, check: () => statistics.blackjacks >= 1 },
        { id: 'blackjacks-5', name: 'Blackjack Hunter', requirement: 5, check: () => statistics.blackjacks >= 5 },
        { id: 'blackjacks-10', name: 'Blackjack Master', requirement: 10, check: () => statistics.blackjacks >= 10 },
        { id: 'blackjacks-25', name: 'Blackjack Champion', requirement: 25, check: () => statistics.blackjacks >= 25 },
        { id: 'blackjacks-50', name: 'Blackjack Legend', requirement: 50, check: () => statistics.blackjacks >= 50 },
        { id: 'wins-10', name: 'First Victories', requirement: 10, check: () => statistics.handsWon >= 10 },
        { id: 'wins-25', name: 'Consistent Winner', requirement: 25, check: () => statistics.handsWon >= 25 },
        { id: 'wins-50', name: 'Winner', requirement: 50, check: () => statistics.handsWon >= 50 },
        { id: 'wins-100', name: 'Victorious', requirement: 100, check: () => statistics.handsWon >= 100 },
        { id: 'wins-250', name: 'Unstoppable', requirement: 250, check: () => statistics.handsWon >= 250 },
        { id: 'win-rate-60', name: 'Above Average', requirement: 60, check: () => statistics.handsPlayed >= 100 && (statistics.handsWon / statistics.handsPlayed) * 100 >= 60 },
        { id: 'win-rate-70', name: 'Expert Level', requirement: 70, check: () => statistics.handsPlayed >= 200 && (statistics.handsWon / statistics.handsPlayed) * 100 >= 70 },
        { id: 'split-hands-5', name: 'Splitter', requirement: 5, check: () => statistics.splitHandsPlayed >= 5 },
        { id: 'split-hands-15', name: 'Split Master', requirement: 15, check: () => statistics.splitHandsPlayed >= 15 },
        { id: 'split-hands-30', name: 'Split Champion', requirement: 30, check: () => statistics.splitHandsPlayed >= 30 },

        // Counting achievements
        { id: 'streak-3', name: 'Counting Beginner', requirement: 3, check: () => statistics.bestStreak >= 3 },
        { id: 'streak-5', name: 'On a Roll', requirement: 5, check: () => statistics.bestStreak >= 5 },
        { id: 'streak-10', name: 'Perfect Memory', requirement: 10, check: () => statistics.bestStreak >= 10 },
        { id: 'streak-15', name: 'Memory Master', requirement: 15, check: () => statistics.bestStreak >= 15 },
        { id: 'streak-25', name: 'Counting Savant', requirement: 25, check: () => statistics.bestStreak >= 25 },
        { id: 'streak-50', name: 'Counting Legend', requirement: 50, check: () => statistics.bestStreak >= 50 },
        { id: 'streak-100', name: 'Count Immortal', requirement: 100, check: () => statistics.bestStreak >= 100 },
        { id: 'accuracy-70', name: 'Decent Counter', requirement: 70, check: () => statistics.handsPlayed >= 10 && statistics.countAccuracy >= 70 },
        { id: 'accuracy-80', name: 'Good Counter', requirement: 80, check: () => statistics.handsPlayed >= 20 && statistics.countAccuracy >= 80 },
        { id: 'accuracy-90', name: 'Sharp Counter', requirement: 90, check: () => statistics.handsPlayed >= 30 && statistics.countAccuracy >= 90 },
        { id: 'accuracy-95', name: 'Expert Counter', requirement: 95, check: () => statistics.handsPlayed >= 50 && statistics.countAccuracy >= 95 },
        { id: 'accuracy-98', name: 'Counting Master', requirement: 98, check: () => statistics.handsPlayed >= 100 && statistics.countAccuracy >= 98 },
        { id: 'no-errors-10', name: 'Flawless Start', requirement: 10, check: () => statistics.consecutiveCorrect >= 10 },
        { id: 'no-errors-25', name: 'Count Perfect', requirement: 25, check: () => statistics.consecutiveCorrect >= 25 },

        // Betting achievements
        { id: 'first-perfect-bet', name: 'Bet Wisely', requirement: 1, check: () => statistics.perfectBets >= 1 },
        { id: 'perfect-bets-5', name: 'Betting Novice', requirement: 5, check: () => statistics.perfectBets >= 5 },
        { id: 'perfect-bets-10', name: 'Smart Bettor', requirement: 10, check: () => statistics.perfectBets >= 10 },
        { id: 'perfect-bets-25', name: 'Betting Expert', requirement: 25, check: () => statistics.perfectBets >= 25 },
        { id: 'perfect-bets-50', name: 'Bet Sizing Pro', requirement: 50, check: () => statistics.perfectBets >= 50 },
        { id: 'perfect-bets-100', name: 'Betting Master', requirement: 100, check: () => statistics.perfectBets >= 100 },
        { id: 'bet-accuracy-80', name: 'Bet Accuracy 80%', requirement: 80, check: () => statistics.handsPlayed >= 20 && statistics.betSizingAccuracy >= 80 },
        { id: 'bet-accuracy-90', name: 'Bet Accuracy 90%', requirement: 90, check: () => statistics.handsPlayed >= 50 && statistics.betSizingAccuracy >= 90 },
        { id: 'bet-accuracy-95', name: 'Bet Accuracy 95%', requirement: 95, check: () => statistics.handsPlayed >= 100 && statistics.betSizingAccuracy >= 95 },
        { id: 'bankroll-10000', name: 'High Roller', requirement: 10000, check: () => statistics.currentBankroll >= 10000 },
        { id: 'bankroll-25000', name: 'Wealthy Gambler', requirement: 25000, check: () => statistics.currentBankroll >= 25000 },
        { id: 'bankroll-50000', name: 'Casino Owner', requirement: 50000, check: () => statistics.currentBankroll >= 50000 },

        // Scenario achievements
        { id: 'first-scenario', name: 'Scenario Novice', requirement: 1, check: () => statistics.scenariosAttempted >= 1 },
        { id: 'scenarios-10', name: 'Scenario Student', requirement: 10, check: () => statistics.scenariosAttempted >= 10 },
        { id: 'scenarios-50', name: 'Scenario Veteran', requirement: 50, check: () => statistics.scenariosAttempted >= 50 },
        { id: 'scenarios-100', name: 'Scenario Expert', requirement: 100, check: () => statistics.scenariosAttempted >= 100 },
        { id: 'scenario-accuracy-80', name: 'Decision Maker', requirement: 80, check: () => statistics.scenariosAttempted >= 10 && (statistics.scenariosCorrect / statistics.scenariosAttempted) * 100 >= 80 },
        { id: 'scenario-accuracy-90', name: 'Strategy Master', requirement: 90, check: () => statistics.scenariosAttempted >= 20 && (statistics.scenariosCorrect / statistics.scenariosAttempted) * 100 >= 90 },
        { id: 'scenario-accuracy-95', name: 'Blackjack Guru', requirement: 95, check: () => statistics.scenariosAttempted >= 30 && (statistics.scenariosCorrect / statistics.scenariosAttempted) * 100 >= 95 },
        { id: 'basic-master', name: 'Basic Strategy Pro', requirement: 20, check: () => statistics.scenariosByCategory['basic']?.correct >= 20 },
        { id: 'deviations-master', name: 'Deviation Expert', requirement: 15, check: () => statistics.scenariosByCategory['deviations']?.correct >= 15 },
        { id: 'insurance-master', name: 'Insurance Ace', requirement: 10, check: () => statistics.scenariosByCategory['insurance']?.correct >= 10 },
      ];

      let newAchievementUnlocked = false;
      const updatedAchievements = [...achievements];

      for (const achievement of allAchievements) {
        const existing = updatedAchievements.find(a => a.achievementId === achievement.id);

        if (!existing && achievement.check()) {
          // New achievement unlocked!
          updatedAchievements.push({
            achievementId: achievement.id,
            currentValue: achievement.requirement,
            unlocked: true,
            unlockedAt: Date.now()
          });
          newAchievementUnlocked = true;
        }
      }

      if (newAchievementUnlocked) {
        sounds.playAchievement();
        set({ achievements: updatedAchievements });
      }
    },

    unlockAchievement: (achievementId) => {
      set((state) => {
        const existing = state.achievements.find(a => a.achievementId === achievementId);
        if (existing) return state;

        const updatedAchievements = [
          ...state.achievements,
          {
            achievementId,
            currentValue: 0,
            unlocked: true,
            unlockedAt: Date.now()
          }
        ];
        sounds.playAchievement();
        return { achievements: updatedAchievements };
      });
    }
  }))
);
